import {
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Icon,
  Text,
  Tooltip,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FiMessageSquare, FiPlus } from 'react-icons/fi';
import { useMySupportTickets } from '../../hooks/useSupportTickets';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { StatusBadge } from '../common/StatusBadge';
import { formatRelativeTime } from '../../utils/formatters';

export default function SupportTicketsList() {
  const { currentUser } = useAuth();
  const { tickets, loading } = useMySupportTickets(currentUser?.uid);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const openCount = tickets.filter((t) => t.status === 'open').length;
  const canCreate = openCount < 2;

  return (
    <Container maxW="3xl" py={6} px={0}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between" align="center">
          <Box>
            <Heading size="lg">Support</Heading>
            <Text color="gray.600" mt={1}>
              {loading ? '' : `${tickets.length} ticket${tickets.length !== 1 ? 's' : ''}`}
            </Text>
          </Box>
          <Tooltip
            label="You can only have 2 open tickets at a time"
            isDisabled={canCreate}
            hasArrow
          >
            <Button
              as={canCreate ? Link : undefined}
              to={canCreate ? '/support/create' : undefined}
              leftIcon={<Icon as={FiPlus} />}
              colorScheme="orange"
              size="sm"
              isDisabled={!canCreate}
            >
              New Ticket
            </Button>
          </Tooltip>
        </HStack>

        {loading ? (
          <LoadingSpinner message="Loading your tickets..." />
        ) : tickets.length === 0 ? (
          <VStack spacing={4}>
            <EmptyState
              title="No support tickets"
              description="Have a question or issue? Create a support ticket and we'll get back to you."
              icon={FiMessageSquare}
            />
            <Button as={Link} to="/support/create" colorScheme="orange">
              Create Your First Ticket
            </Button>
          </VStack>
        ) : (
          <VStack spacing={3} align="stretch">
            {tickets.map((ticket) => (
              <Box
                key={ticket.id}
                as={Link}
                to={`/support/${ticket.id}`}
                bg={cardBg}
                rounded="lg"
                borderWidth={1}
                borderColor={borderColor}
                p={4}
                _hover={{ borderColor: 'orange.300', shadow: 'sm' }}
                transition="all 0.2s"
                display="block"
              >
                <HStack justify="space-between" align="start" mb={2}>
                  <Text fontWeight="semibold" fontSize="md" noOfLines={1} flex={1}>
                    {ticket.subject}
                  </Text>
                  <StatusBadge status={ticket.status} />
                </HStack>
                <Text fontSize="sm" color="gray.500" noOfLines={2} mb={2}>
                  {ticket.message}
                </Text>
                <Text fontSize="xs" color="gray.400">
                  {formatRelativeTime(ticket.createdAt)}
                </Text>
              </Box>
            ))}
          </VStack>
        )}
      </VStack>
    </Container>
  );
}
